import React from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { createThemedStyles } from '../theme/useThemedStyles';
import AdBanner from '../components/AdBanner';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';

const FAQScreen = () => {
  const styles = useFAQStyles();

  return (
    <WebPage title="FAQ">
    <PageScroll style={styles.container}>
      <Text style={styles.title}>🟢 GoalMint App – Frequently Asked Questions (FAQ)</Text>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>🧭 General</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What is GoalMint?</Text>
          <Text style={styles.answer}>A: GoalMint is a personal finance app that helps you manage and allocate your income across multiple financial goals — like saving for a house, paying off debt, or building an emergency fund — using prioritization and visual planning tools.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Who is GoalMint for?</Text>
          <Text style={styles.answer}>A: GoalMint is perfect for individuals or families who want to take control of their finances by setting clear goals and seeing how their income supports each one. No advanced finance knowledge required!</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📊 Dashboard</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What displays on the Dashboard?</Text>
          <Text style={styles.answer}>A: Dashboard presents you with summary view of your Monthly Income, Total Saved and all active goals with their current status.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Can I edit Goals on Dashboard?</Text>
          <Text style={styles.answer}>A: No, Goals can be edited or deleted on My Goals tab.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📈 Analytics Dashboard</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What is the Analytics Dashboard?</Text>
          <Text style={styles.answer}>A: The Analytics Dashboard provides visual insights into your financial goals, spending, and savings trends over time.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How do I use the Analytics Dashboard?</Text>
          <Text style={styles.answer}>A: Access the Analytics Dashboard from the main navigation. You can view charts and breakdowns of your progress, income, and allocations.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Can I customize the analytics shown?</Text>
          <Text style={styles.answer}>A: Currently, the Analytics Dashboard displays standard charts, but more customization options are coming soon!</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What does Goal Performance show?</Text>
          <Text style={styles.answer}>A: Goal Performance displays how each of your goals is progressing toward its target, helping you quickly identify which goals are on track and which may need more attention.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What is Success by Priority?</Text>
          <Text style={styles.answer}>A: Success by Priority breaks down your goals by their assigned priority level, showing which high-priority goals are being achieved and which ones may require more focus or resources.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What does Performance by Type mean?</Text>
          <Text style={styles.answer}>A: Performance by Type groups your goals into categories like Savings, Debt, and Investment, so you can see how you're performing in each area of your financial life.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What kind of Financial Insights are provided?</Text>
          <Text style={styles.answer}>A: The Analytics Dashboard offers insights such as your savings rate, debt payoff trends, and allocation efficiency, helping you make smarter financial decisions.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What is Risk Assessment in Analytics?</Text>
          <Text style={styles.answer}>A: Risk Assessment highlights potential risks, such as underfunded goals or missed deadlines, so you can take action to stay on track with your financial plan.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>🎯 Goals</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How do I add a goal?</Text>
          <Text style={styles.answer}>A: Go to My Goals tab and tap the "+ Add Goal" button. You{"'"}ll be asked to enter the goal name, Priority Level, target amount, Current amount, Monthly income allocation or Percentage income allocation.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Can I edit or delete a goal later?</Text>
          <Text style={styles.answer}>A: Yes! Just tap on any goal card in the Goals tab to edit or delete it.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What does "priority" mean in goals?</Text>
          <Text style={styles.answer}>A: Priority helps you to identify goals to allocate more income to.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>💸 Income & Allocation</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How do I enter my income?</Text>
          <Text style={styles.answer}>A: Go to the Income & Allocation tab and enter your income manually. GoalMint uses this to suggest how much to allocate to each goal.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>⏳ Progress</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What does the progress bar on my goals mean?</Text>
          <Text style={styles.answer}>A: It shows how much you{"'"}ve saved vs. the goal target — both as a percentage and dollar value.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>🎨 Appearance & Theme</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How do I change the app theme?</Text>
          <Text style={styles.answer}>A: Go to Settings {'>'} Appearance. Choose Light or Dark mode. Light uses a navy sidebar with a light workspace; Dark applies a darker palette across the app.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Can I change the accent color?</Text>
          <Text style={styles.answer}>A: Yes. In Settings {'>'} Appearance {'>'} Theme Color, pick an accent such as GoalMint (original green), Purple, Pink, Royal Blue, Navy, Orange, Teal, Red, Amber, or Indigo. Accent colors update buttons, highlights, and accents throughout the app.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Do theme settings sync across devices?</Text>
          <Text style={styles.answer}>A: Theme mode and accent color are saved on your device and in your account settings, so they apply each time you sign in on that device.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>⚙️ Settings & Security</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What currency does GoalMint use?</Text>
          <Text style={styles.answer}>A: You can set your preferred currency and date format in Settings {'>'} Currency & Region.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Can I back up or export my data?</Text>
          <Text style={styles.answer}>A: Yes. Standard and Premium subscribers can export from Settings {'>'} Data & Backup. Standard includes CSV export. Premium adds Excel and PDF export. Free plan users can upgrade from the Subscription screen to unlock exports.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Is my data secure?</Text>
          <Text style={styles.answer}>A: Yes. Your data is stored securely and synced with Google Cloud Firebase when you are signed in. You can also add an extra layer of protection with App Lock or biometric unlock in Settings {'>'} Privacy & Security.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What is App Lock (PIN)?</Text>
          <Text style={styles.answer}>A: App Lock lets you protect GoalMint with a 4-digit PIN. Enable it in Settings {'>'} Privacy & Security, then tap Set PIN or Change PIN. When App Lock is on, you will be asked for your PIN when returning to the app.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What is Fingerprint Unlock?</Text>
          <Text style={styles.answer}>A: On supported phones and tablets, Fingerprint Unlock uses your device biometrics (such as Touch ID, Face ID, or fingerprint) to unlock GoalMint quickly. Enable it in Settings {'>'} Privacy & Security. You must authenticate once to turn it on.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: Can I use both PIN and Fingerprint Unlock?</Text>
          <Text style={styles.answer}>A: No. App Lock (PIN) and Fingerprint Unlock are mutually exclusive — turning one on turns the other off. Choose the option that works best for your device.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How often do I need to unlock the app?</Text>
          <Text style={styles.answer}>A: After you unlock with your PIN or biometrics, you will not need to enter your username and password again for up to 4 weeks. The lock screen may still appear when you return to the app after it has been in the background.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>🔔 Notifications</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What notifications can GoalMint send?</Text>
          <Text style={styles.answer}>A: GoalMint can notify you about goal progress, approaching deadlines, and payment reminders. Manage these in Settings under Notifications and Income & Payment Preferences.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How do I turn notifications off?</Text>
          <Text style={styles.answer}>A: Use the toggles in Settings to disable goal progress, deadline, or payment reminders. You can also turn off income reminders separately under Income & Payment Preferences.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>💳 Subscription</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What are the available subscription tiers?</Text>
          <Text style={styles.answer}>A: GoalMint offers Free, Standard, and Premium tiers. Free supports up to 3 goals with basic features. Standard supports up to 10 goals, ad-free use, enhanced analytics, and CSV export. Premium supports up to 25 goals, advanced insights, and CSV, Excel, and PDF export. Compare plans in Settings {'>'} Subscription.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How do I upgrade or manage my subscription?</Text>
          <Text style={styles.answer}>A: Go to Settings {'>'} Subscription to view plans, subscribe, or compare features. On iPhone and iPad you can subscribe through the App Store. On Android, use Google Play. On the web app, subscribe through secure web checkout. Use Manage/Cancel Subscription to open your store billing settings.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: I subscribed but my plan did not update. What should I do?</Text>
          <Text style={styles.answer}>A: Open Settings {'>'} Subscription and tap Restore Purchases. Make sure you are signed in with the same account you used to subscribe. Plan changes can take a few seconds to sync after purchase, especially on iOS.</Text>
        </View>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: What happens if my subscription expires?</Text>
          <Text style={styles.answer}>A: If your subscription expires, you are moved to the Free tier and may lose access to paid features such as extra goals, analytics, and exports. Your goals and data remain saved in your account.</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📬 Support</Text>
        <View style={styles.qa}>
          <Text style={styles.question}>Q: How can I contact support?</Text>
          <Text style={styles.answer}>A: Go to Settings {'>'}  Support to access our email or send us a message</Text>
        </View>
      </View>
      <AdBanner />
    </PageScroll>
    </WebPage>
  );
};

const useFAQStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.grey[100],
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary.main,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary.main,
    marginBottom: 16,
  },
  qa: {
    marginBottom: 16,
  },
  question: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 8,
  },
  answer: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
  },
}));

export default FAQScreen; 